import { useState, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Truck, IdCard, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useConsent } from "@/context/ConsentContext";

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

const MODES = [
  { value: "quote",   label: "Freight Quote Request", icon: Truck,          desc: "Get pricing and availability for a load.",     endpoint: "/quote-requests" },
  { value: "driver",  label: "Driver Application",    icon: IdCard,         desc: "Apply to drive for Lucas Trans Inc.",           endpoint: "/driver-applications" },
  { value: "message", label: "General Message",       icon: MessageSquare,  desc: "Ask a question or send us a note.",             endpoint: "/contact-messages" },
];

const QUOTE_EQUIPMENT_DEFAULT = ["Dry Van", "Refrigerated (Reefer)", "Flatbed", "Power Only", "Other"];
const CDL_CLASS_DEFAULT = ["A"];
const MSG_CATEGORIES  = ["Freight Quote", "Shipment Questions", "Driver / Career Questions", "General"];

const DEFAULT_HERO = "https://images.pexels.com/photos/17377416/pexels-photo-17377416.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1600";

const initialForms = {
  quote:  { contact_name: "", contact_phone: "", contact_email: "", company: "", origin: "", destination: "", equipment_type: "Dry Van", weight_lbs: 0, pickup_date: "", commodity: "", notes: "" },
  driver: { full_name: "", phone: "", phone_secondary: "", email: "", city_state: "", cdl_class: "A", years_experience: 0, driving_experience: "", endorsements: "", current_employment: "", available_start_date: "", resume_url: "", message: "", consent_accepted: false },
  message:{ name: "", email: "", phone: "", category: "General", message: "" },
};

export default function Contact({ initialMode = "message" }) {
  const { info = {} } = useOutletContext() || {};
  const [mode, setMode] = useState(initialMode);
  const [forms, setForms] = useState(initialForms);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { accepted, reopen } = useConsent();

  const active = MODES.find((m) => m.value === mode);
  const form = forms[mode];
  const setForm = (patch) => setForms({ ...forms, [mode]: { ...forms[mode], ...patch } });
  const upd = (k) => (e) => setForm({ [k]: e.target.value });

  const heroImg = resolveUrl(info.careers_hero_image_url) || DEFAULT_HERO;
  const equipmentOptions = (info.freight_equipment_options && info.freight_equipment_options.length) ? info.freight_equipment_options : QUOTE_EQUIPMENT_DEFAULT;
  const cdlOptions = (info.cdl_class_options && info.cdl_class_options.length) ? info.cdl_class_options : CDL_CLASS_DEFAULT;

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) { reopen(); return; }
    setLoading(true);
    setSent(false);
    try {
      let payload = { ...form };
      if (mode === "quote")  payload.weight_lbs = Number(payload.weight_lbs) || 0;
      if (mode === "driver") payload.years_experience = Number(payload.years_experience) || 0;
      await api.post(active.endpoint, payload);
      setSent(true);
      setForms({ ...forms, [mode]: initialForms[mode] });
      toast.success(
        mode === "quote"  ? "Quote request received — a dispatcher will be in touch."
      : mode === "driver" ? "Application submitted. Our recruiting team will follow up."
      :                     "Message sent. We'll be in touch shortly."
      );
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const hero = useMemo(() => {
    if (mode === "quote")  return { eyebrow: "// Freight Quote",   title: "Request a Freight Quote.", sub: "Tell us about your load — Lucas Trans dispatch will follow up with pricing and availability." };
    if (mode === "driver") return { eyebrow: "// CDL Careers",     title: "Drive With Lucas Trans Inc.", sub: "Respect. Great equipment. Professional dispatch. Apply today." };
    return                        { eyebrow: "// Contact",         title: "Talk to Dispatch.", sub: "Whether you are a shipper, driver, or partner — we would love to hear from you." };
  }, [mode]);

  return (
    <div className="bg-[var(--lt-bg)]">
      {/* HERO */}
      <section data-lt-scheme="dark" className="relative h-[46vh] min-h-[380px]">
        <img src={heroImg} alt="Lucas Trans Inc." className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 lt-hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-12">
          <div className="font-mono-lt text-xs uppercase tracking-[0.3em] text-[var(--lt-accent-bright)] mb-3">{hero.eyebrow}</div>
          <h1 className="font-display font-black text-5xl md:text-6xl text-white uppercase leading-none">{hero.title}</h1>
          <p className="text-slate-300 mt-3 max-w-2xl">{hero.sub}</p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Contact info side-panel */}
          <aside className="space-y-4">
            {info.phone && <InfoBlock icon={Phone} label={info.phone_label || "Primary Dispatch"} value={info.phone} href={`tel:${info.phone}`} testid="contact-phone-primary" />}
            {info.phone_secondary && <InfoBlock icon={Phone} label={info.phone_secondary_label || "Alternate"} value={info.phone_secondary} href={`tel:${info.phone_secondary}`} testid="contact-phone-secondary" />}
            {!info.phone && !info.phone_secondary && <InfoBlock icon={Phone} label="Phone" value="To be provided" />}
            {info.email && <InfoBlock icon={Mail} label={info.email_label || "Email"} value={info.email} href={`mailto:${info.email}`} testid="contact-email-primary" />}
            {info.email_secondary && <InfoBlock icon={Mail} label={info.email_secondary_label || "Alternate Email"} value={info.email_secondary} href={`mailto:${info.email_secondary}`} testid="contact-email-secondary" />}
            {!info.email && !info.email_secondary && <InfoBlock icon={Mail} label="Email" value="To be provided" />}
            <InfoBlock icon={MapPin} label="Address" value={info.address_line || "Hoffman Estates, Illinois"} />
            <InfoBlock icon={Clock} label="Hours" value={info.hours || "To be provided"} />
          </aside>

          {/* Unified form */}
          <div className="lg:col-span-2 border border-[var(--lt-border)] bg-[var(--lt-panel)] p-8 md:p-10 rounded-2xl" data-testid="contact-unified-form">
            {/* Mode selector */}
            <Label className="text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-400 mb-2 block">I want to…</Label>
            <Select value={mode} onValueChange={(v) => { setSent(false); setMode(v); }}>
              <SelectTrigger data-testid="contact-mode-select" className="bg-[var(--lt-bg)] border-[var(--lt-border)] h-12 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODES.map((m) => {
                  const I = m.icon;
                  return (
                    <SelectItem key={m.value} value={m.value} data-testid={`contact-mode-${m.value}`}>
                      <span className="inline-flex items-center gap-2"><I className="h-4 w-4" /> {m.label}</span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <div className="text-xs text-slate-500 mt-2">{active.desc}</div>

            {sent && (
              <div className="mt-6 border border-emerald-800 bg-emerald-950/20 text-emerald-300 p-4 text-sm rounded-lg" data-testid="contact-success">
                Thanks — we received your submission and will be in touch shortly.
              </div>
            )}

            {!accepted && (
              <div className="mt-6 border border-[var(--lt-accent)] bg-[var(--lt-bg)] text-[var(--lt-text,inherit)] p-4 text-sm rounded-lg flex flex-wrap items-center justify-between gap-3" data-testid="consent-required-notice">
                <span>Please accept our Privacy Policy and Terms &amp; Conditions to use this form.</span>
                <Button type="button" size="sm" onClick={reopen} data-testid="consent-review-btn" className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white rounded-lg">Review &amp; Accept</Button>
              </div>
            )}

            <form onSubmit={submit} aria-disabled={!accepted} className={`mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 ${accepted ? "" : "opacity-50 pointer-events-none select-none"}`}>
              {mode === "quote" && (
                <>
                  <F label="Contact Name" required><Input required data-testid="q-name"     value={form.contact_name}  onChange={upd("contact_name")} /></F>
                  <F label="Company"><Input                        data-testid="q-company"  value={form.company}       onChange={upd("company")} /></F>
                  <F label="Phone" required><Input required        data-testid="q-phone"    value={form.contact_phone} onChange={upd("contact_phone")} /></F>
                  <F label="Email" required><Input required type="email" data-testid="q-email" value={form.contact_email} onChange={upd("contact_email")} /></F>
                  <F label="Origin (City, State or Zip)" required><Input required data-testid="q-origin"      value={form.origin}      onChange={upd("origin")} /></F>
                  <F label="Destination (City, State or Zip)" required><Input required data-testid="q-destination" value={form.destination} onChange={upd("destination")} /></F>
                  <F label="Equipment">
                    <Select value={form.equipment_type} onValueChange={(v) => setForm({ equipment_type: v })}>
                      <SelectTrigger data-testid="q-equipment" className="bg-[var(--lt-bg)] border-[var(--lt-border)] h-11 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>{equipmentOptions.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Weight (lbs)"><Input type="number" min="0" data-testid="q-weight"    value={form.weight_lbs}  onChange={upd("weight_lbs")} /></F>
                  <F label="Pickup Date"><Input type="date"           data-testid="q-date"      value={form.pickup_date} onChange={upd("pickup_date")} /></F>
                  <F label="Commodity"><Input                         data-testid="q-commodity" value={form.commodity}   onChange={upd("commodity")} /></F>
                  <F label="Notes / Special Requirements" full><Textarea rows={4} data-testid="q-notes" value={form.notes} onChange={upd("notes")} /></F>
                  <SubmitRow loading={loading} testid="q-submit" label="Submit Quote Request" />
                </>
              )}

              {mode === "driver" && (
                <>
                  <F label="Full Name" required><Input required data-testid="app-name"  value={form.full_name} onChange={upd("full_name")} /></F>
                  <F label="Primary Phone" required><Input required data-testid="app-phone" value={form.phone} onChange={upd("phone")} /></F>
                  <F label="Secondary Phone (optional)"><Input data-testid="app-phone-secondary" value={form.phone_secondary} onChange={upd("phone_secondary")} /></F>
                  <F label="Email" required><Input required type="email" data-testid="app-email" value={form.email} onChange={upd("email")} /></F>
                  <F label="City & State" required><Input required data-testid="app-city" value={form.city_state} onChange={upd("city_state")} /></F>
                  <F label="CDL Class">
                    <Select value={form.cdl_class} onValueChange={(v) => setForm({ cdl_class: v })}>
                      <SelectTrigger data-testid="app-cdl-class" className="bg-[var(--lt-bg)] border-[var(--lt-border)] h-11 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                      {cdlOptions.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}
                    </SelectContent>
                    </Select>
                  </F>
                  <F label="Years of Experience"><Input type="number" min="0" data-testid="app-years" value={form.years_experience} onChange={upd("years_experience")} /></F>
                  <F label="Endorsements (Hazmat, Tanker, Doubles/Triples)"><Input data-testid="app-endorsements" value={form.endorsements} onChange={upd("endorsements")} /></F>
                  <F label="Current Employment"><Input data-testid="app-employment" value={form.current_employment} onChange={upd("current_employment")} /></F>
                  <F label="Available Start Date"><Input type="date" data-testid="app-start-date" value={form.available_start_date} onChange={upd("available_start_date")} /></F>
                  <F label="Resume URL (Google Drive, Dropbox, etc.)"><Input data-testid="app-resume" value={form.resume_url} onChange={upd("resume_url")} /></F>
                  <F label="Driving Experience Summary" full><Textarea rows={3} data-testid="app-driving-exp" value={form.driving_experience} onChange={upd("driving_experience")} /></F>
                  <F label="Message" full><Textarea rows={3} data-testid="app-message" value={form.message} onChange={upd("message")} /></F>

                  {/* --- Driver Data Consent gate — required by /api/driver-applications --- */}
                  <div className="md:col-span-2 border border-[var(--lt-border)] bg-[var(--lt-bg)] p-4 rounded-xl">
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-block">
                      <input
                        type="checkbox"
                        checked={!!form.consent_accepted}
                        onChange={(e) => setForm({ consent_accepted: e.target.checked })}
                        data-testid="consent-checkbox"
                        className="mt-1 h-4 w-4 accent-[var(--lt-accent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--lt-accent)]"
                        aria-describedby="consent-hint"
                      />
                      <span className="text-sm text-[color:var(--lt-text)] leading-relaxed">
                        <strong>I agree to the Driver Data Consent statement.</strong>{" "}
                        I certify the information above is accurate and I authorize Lucas Trans Inc. to
                        contact me by phone, SMS or email about this application.{" "}
                        <a
                          href="/driver-consent"
                          target="_blank"
                          rel="noopener"
                          className="text-[var(--lt-accent-bright)] underline"
                          data-testid="consent-read-link"
                        >
                          Read the full statement
                        </a>.
                      </span>
                    </label>
                    <div id="consent-hint" className="text-xs text-slate-500 mt-2 pl-7">
                      Required. If you decline, we can&apos;t process your application — you can still browse the site.
                    </div>
                  </div>

                  <div className="md:col-span-2 pt-2 flex flex-wrap items-center gap-3">
                    <Button
                      type="submit"
                      disabled={loading || !form.consent_accepted}
                      data-testid="app-submit"
                      className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white rounded-xl h-12 px-8 font-head uppercase tracking-widest text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {loading ? "Sending…" : "Submit Application"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setForm(initialForms.driver); toast.info("Application cleared."); }}
                      data-testid="app-decline"
                      className="bg-transparent border-[var(--lt-border)] hover:bg-[var(--lt-panel-hover)] rounded-xl h-12 px-6 font-head uppercase tracking-widest text-xs"
                    >
                      Decline &amp; Clear
                    </Button>
                  </div>
                </>
              )}

              {mode === "message" && (
                <>
                  <F label="Name" required><Input required data-testid="contact-name" value={form.name} onChange={upd("name")} /></F>
                  <F label="Email" required><Input required type="email" data-testid="contact-email" value={form.email} onChange={upd("email")} /></F>
                  <F label="Phone"><Input data-testid="contact-phone" value={form.phone} onChange={upd("phone")} /></F>
                  <F label="I'm contacting about">
                    <Select value={form.category} onValueChange={(v) => setForm({ category: v })}>
                      <SelectTrigger data-testid="contact-category" className="bg-[var(--lt-bg)] border-[var(--lt-border)] h-11 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>{MSG_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Message" full><Textarea required rows={5} data-testid="contact-message" value={form.message} onChange={upd("message")} /></F>
                  <SubmitRow loading={loading} testid="contact-submit" label="Send Message" />
                </>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoBlock({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="border border-[var(--lt-border)] bg-[var(--lt-panel)] p-6 rounded-xl hover:border-[var(--lt-accent)] transition-colors" data-testid={testid}>
      <Icon className="h-5 w-5 text-[var(--lt-accent-bright)] mb-3" />
      <div className="text-xs font-mono-lt uppercase tracking-[0.25em] text-slate-500">{label}</div>
      <div className="mt-1 font-head break-words">{value}</div>
    </div>
  );
  return href ? <a href={href} className="block">{inner}</a> : inner;
}

function F({ label, children, required, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-400 mb-2 block">
        {label}{required && <span className="text-[var(--lt-accent-bright)]"> *</span>}
      </Label>
      <div className="[&_input]:bg-[var(--lt-bg)] [&_input]:border-[var(--lt-border)] [&_input]:rounded-xl [&_input]:h-11 [&_textarea]:bg-[var(--lt-bg)] [&_textarea]:border-[var(--lt-border)] [&_textarea]:rounded-xl">
        {children}
      </div>
    </div>
  );
}

function SubmitRow({ loading, label, testid }) {
  return (
    <div className="md:col-span-2 pt-2">
      <Button type="submit" disabled={loading} data-testid={testid} className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white rounded-xl h-12 px-8 font-head uppercase tracking-widest text-sm">
        {loading ? "Sending…" : label}
      </Button>
    </div>
  );
}
